import { authAPI } from '../api/auth';
import { usersAPI } from '../api/users';
import {
  analyzeImage,
  getColourProfiles,
  getColourProfile,
  deleteColourProfile,
} from '../api/colours';
import { moodBoardAPI } from '../api/mood_boards';
import { onboardingAPI } from '../api/onboarding';
import { subscriptionAPI } from '../api/subscriptions';
import { runFullConnectionTest } from '../utils/connectionTest';
import { logger } from '../utils/logger';
import { API_URL, MOCK_MODE, DEBUG_API } from '../constants/config';

export class BackendService {
  private static instance: BackendService;

  private constructor() {}

  /**
   * Get singleton instance of BackendService
   */
  public static getInstance(): BackendService {
    if (!BackendService.instance) {
      BackendService.instance = new BackendService();
    }
    return BackendService.instance;
  }

  /**
   * Initialize the backend service and run connection tests
   */
  public async initialize(): Promise<boolean> {
    if (DEBUG_API) {
      logger.log('Initializing backend service...');
    }

    try {
      // Run connection tests to verify backend is accessible
      const connectionTest = await runFullConnectionTest();

      if (!connectionTest.overall && !MOCK_MODE) {
        logger.warn(
          '⚠️ Backend connection issues detected:',
          connectionTest.summary
        );
        return false;
      }

      if (DEBUG_API) {
        logger.log('✅ Backend Service initialized successfully');
      }

      return true;
    } catch (error) {
      logger.error('❌ Failed to initialize Backend Service:', error);
      return false;
    }
  }

  // Authentication Services
  public auth = {
    /**
     * Register a new user
     */
    register: authAPI.register,

    /**
     * Login user
     */
    login: authAPI.login,

    /**
     * Get current user information
     */
    getCurrentUser: authAPI.getCurrentUser,

    /**
     * Check backend health
     */
    healthCheck: authAPI.healthCheck,
  };

  // User Management Services
  public users = {
    /**
     * Get user profile
     */
    getProfile: usersAPI.getProfile,

    /**
     * Update user profile
     */
    updateProfile: usersAPI.updateProfile,

    /**
     * Get all users (admin only)
     */
    getAllUsers: usersAPI.getAllUsers,
  };

  // Colour Analysis Services
  public colours = {
    /**
     * Analyze image for colour information
     */
    analyze: analyzeImage,

    /**
     * Get all colour profiles
     */
    getProfiles: getColourProfiles,

    /**
     * Get specific colour profile
     */
    getProfile: getColourProfile,

    /**
     * Delete colour profile
     */
    deleteProfile: deleteColourProfile,
  };

  // Mood Board Services
  public moodBoards = {
    /**
     * Create a new mood board
     */
    create: moodBoardAPI.create,

    /**
     * Get mood boards with filtering
     */
    getMoodBoards: moodBoardAPI.getMoodBoards,

    /**
     * Get a specific mood board
     */
    getMoodBoard: moodBoardAPI.getMoodBoard,

    /**
     * Update a mood board
     */
    update: moodBoardAPI.update,

    /**
     * Delete a mood board
     */
    delete: moodBoardAPI.delete,

    /**
     * Toggle like/unlike for a mood board
     */
    toggleLike: moodBoardAPI.toggleLike,

    /**
     * Get public mood boards for community
     */
    getPublicMoodBoards: moodBoardAPI.getPublicMoodBoards,

    /**
     * Get user's own mood boards
     */
    getUserMoodBoards: moodBoardAPI.getUserMoodBoards,
  };

  // Onboarding Services
  public onboarding = {
    /**
     * Complete user onboarding
     */
    complete: onboardingAPI.complete,

    /**
     * Get onboarding status
     */
    getStatus: onboardingAPI.getStatus,
  };

  // Subscription Services
  public subscriptions = {
    /**
     * Get subscription status
     */
    getStatus: subscriptionAPI.getStatus,

    /**
     * Get usage and remaining credits
     */
    getUsage: subscriptionAPI.getUsage,

    /**
     * Get plan limits and features (public)
     */
    getPlans: subscriptionAPI.getPlans,

    /**
     * Create payment checkout; returns checkout_url for redirect
     */
    checkout: subscriptionAPI.checkout,

    /**
     * Upgrade to Plus (stub when payment provider not configured)
     */
    upgradeToPlus: subscriptionAPI.upgradeToPlus,

    /**
     * Upgrade to Pro
     */
    upgrade: subscriptionAPI.upgradeToPro,

    /**
     * Start freemium trial
     */
    startFreemiumTrial: subscriptionAPI.startFreemiumTrial,

    /**
     * Downgrade to free
     */
    downgrade: subscriptionAPI.downgradeToFree,

    /**
     * Purchase pay-per-scan
     */
    purchasePayPerScan: subscriptionAPI.purchasePayPerScan,

    /**
     * Check trial expiration
     */
    checkTrialExpiration: subscriptionAPI.checkTrialExpiration,

    /**
     * Restore subscription
     */
    restore: subscriptionAPI.restore,
  };

  // Utility Services
  public utils = {
    /**
     * Run comprehensive connection test
     */
    testConnection: runFullConnectionTest,

    /**
     * Get backend configuration info
     */
    getConfig: () => ({
      API_URL,
      MOCK_MODE,
      DEBUG_API,
    }),

    /**
     * Check if backend is accessible
     */
    isBackendAvailable: async (): Promise<boolean> => {
      try {
        await this.auth.healthCheck();
        return true;
      } catch {
        return false;
      }
    },
  };

  // Error handling utilities
  public handleError = (error: unknown, context: string = 'Unknown') => {
    // Extract meaningful error message
    let message = 'An error occurred';

    const err = error as {
      response?: { data?: { detail?: string } };
      message?: string;
    };
    if (err?.response?.data?.detail) {
      message = err.response.data.detail;
    } else if (err?.message) {
      message = err.message;
    } else if (typeof error === 'string') {
      message = error;
    }

    return {
      success: false,
      error: message,
      context,
      timestamp: new Date().toISOString(),
    };
  };
}

// Export singleton instance
export const backendService = BackendService.getInstance();
